import type { Option } from '@byecode/ui'
import { type DataSourceAbstract, DataSourceType, FlowSelectSourceType, IndependentAction } from '@lighthouse/core'
import type { JSONContent } from '@tiptap/core'
import { find } from 'rambda'

import { nodeTypeIconMap, nodeTypeIconPlatformMap } from '../constants'
import { FlowControlNodeType, type FlowNode, LoopTypeEnum, type NodeOption } from '../types'

export const getElseHandleId = () => {
    return `handle-else`
}

export const getUpstreamRealDsId = (nodeId: string, allParentNodes: undefined | FlowNode[]): string | undefined => {
    if (!allParentNodes) {
        return
    }
    const node = allParentNodes.find(n => n.id === nodeId)
    if (!node || !node.data) {
        return undefined
    }
    const nodeData = node.data
    if (
        node.data.nodeType === FlowControlNodeType.LOOP_ACTION &&
        node.data.config.loopType === LoopTypeEnum.BASE_ON_RECORDS &&
        node.data.config?.recordsConfig?.nodeId
    ) {
        return getUpstreamRealDsId(node.data.config.recordsConfig.nodeId, allParentNodes)
    }

    if (
        nodeData.config &&
        'selectType' in nodeData.config &&
        nodeData.config.selectType === FlowSelectSourceType.UPSTREAM &&
        nodeData.config &&
        'nodeId' in nodeData.config &&
        typeof nodeData.config.nodeId === 'string'
    ) {
        return getUpstreamRealDsId(nodeData.config.nodeId, allParentNodes)
    }

    if (nodeData.config && 'dataSourceId' in nodeData.config && typeof nodeData.config.dataSourceId === 'string') {
        return nodeData.config.dataSourceId
    }

    return undefined
}

type GetUpstreamNodeOptionPayload = {
    allParentNodes: FlowNode[]
    dataSourceList: DataSourceAbstract[]
    filter?: (node: FlowNode) => boolean
    isIncludeAggregateDataSource?: boolean
}
// 根据disabledFilter 获取可以使用的上游节点
export const getUpstreamNodeOption = (payload: GetUpstreamNodeOptionPayload) => {
    const { allParentNodes, dataSourceList, filter, isIncludeAggregateDataSource } = payload
    return allParentNodes.reduce<Option[]>((prev, cur) => {
        if (filter && !filter(cur)) {
            return prev
        }
        const dsId = getUpstreamRealDsId(cur.id, allParentNodes)
        if (dsId) {
            const dataSource = find(item => item.id === dsId, dataSourceList)
            if (!dataSource || (!isIncludeAggregateDataSource && dataSource.type === DataSourceType.aggregateDataSource)) {
                return prev
            }
        }

        prev.push({
            label: cur.data.name,
            value: cur.id,
            icon: getFlowNodeIcon(cur.data)
        })
        return prev
    }, [])
}

export const getNodeDataSourceId: (node: FlowNode, nodes: FlowNode[]) => string = (node, nodes) => {
    if (!node.data || !node.data.config) {
        return ''
    }

    if ('selectType' in node.data.config) {
        if (node.data.config.selectType === FlowSelectSourceType.UPSTREAM && 'nodeId' in node.data.config) {
            const { nodeId } = node.data.config
            const parentNodeConfigNode = find(item => item.id === nodeId, nodes)
            if (parentNodeConfigNode) {
                return getNodeDataSourceId(parentNodeConfigNode, nodes)
            }
            return ''
        }
        if (node.data.config.selectType === FlowSelectSourceType.DATASOURCE && 'dataSourceId' in node.data.config) {
            return node.data.config.dataSourceId ?? ''
        }
        return ''
    }
    if ('dataSourceId' in node.data.config && node.data.config.dataSourceId) {
        return node.data.config.dataSourceId ?? ''
    }
    return ''
}

export const getActionDataSourceId: (action: IndependentAction, actions: IndependentAction[]) => string = (action, actions) => {
    if (!action.data || !action.type) {
        return ''
    }
    if (action.type === 'updateRecord') {
        const config = action.data[action.type]
        return config.dataSourceId
    }

    return ''

    // if ('selectType' in config) {
    //     if (config.selectType === FlowSelectSourceType.UPSTREAM && 'nodeId' in node.data.config) {
    //         const { nodeId } = node.data.config
    //         const parentNodeConfigNode = find(item => item.id === nodeId, nodes)
    //         if (parentNodeConfigNode) {
    //             return getNodeDataSourceId(parentNodeConfigNode, nodes)
    //         }
    //         return ''
    //     }
    //     if (node.data.config.selectType === FlowSelectSourceType.DATASOURCE && 'dataSourceId' in node.data.config) {
    //         return node.data.config.dataSourceId ?? ''
    //     }
    //     return ''
    // }
    // if ('dataSourceId' in node.data.config && node.data.config.dataSourceId) {
    //     return node.data.config.dataSourceId ?? ''
    // }
    return ''
}

export const parseJsonExpression = (json: JSONContent | undefined) => {
    if (!json) {
        return ''
    }
    return (
        json.content?.reduce((prev, cur, index) => {
            if (cur?.content) {
                const text = parseJsonExpression(cur)
                prev += text
                return prev
            }
            if (cur?.type === 'text') {
                prev += cur.text
                return prev
            }
            if (cur?.type === 'variable' && cur.attrs?.value) {
                const value = JSON.stringify(cur.attrs.value)
                const dataValue = value.replaceAll('"', '&quot;')
                prev += `<variable data-value="${dataValue}"></variable>`
            }
            return prev
        }, '') || ''
    )
}

// 提取查找节点选项的逻辑
export const findNodeOption = (nodeData: FlowNode['data'], options: NodeOption[]) => {
    if (nodeData.nodeType === 'SEND_EMAIL_ACTION') {
        return options.find(item => item.subType === nodeData.config.senderAccountType)
    }
    return options.find(item => item.value === nodeData.nodeType)
}

// 提取设置图标的逻辑
export const getFlowNodeIcon = (nodeData: FlowNode['data']) => {
    if (nodeData.nodeType === 'SEND_EMAIL_ACTION') {
        return nodeTypeIconPlatformMap[nodeData.config.senderAccountType]
    }
    return nodeTypeIconMap[nodeData.nodeType]
}
